"use client";

import { useCallback, useEffect, useState } from "react";
import type { Editor } from "@tiptap/react";
import { cn } from "@workspace/ui/lib/utils";

export interface OutlineItem {
  /** ProseMirror position of the heading node. Changes on every edit above it. */
  pos: number;
  level: number;
  text: string;
}

/**
 * Sections for the navigator: H2s are top-level entries, and H3/H4 nest under
 * the H2 before them, like the Google Docs outline. H1 is left out because
 * the title bar already carries the document's name, and imported docs often
 * repeat it as an H1. H5/H6 are left out because they are too fine-grained
 * to navigate by.
 */
const OUTLINE_LEVELS = new Set([2, 3, 4]);

function readOutline(editor: Editor): OutlineItem[] {
  const items: OutlineItem[] = [];
  editor.state.doc.descendants((node, pos) => {
    if (node.type.name !== "heading") return;
    const level = Number(node.attrs.level);
    const text = node.textContent.trim();
    if (OUTLINE_LEVELS.has(level) && text) items.push({ pos, level, text });
    // Headings hold only inline content, so there is nothing inside to visit.
    return false;
  });
  return items;
}

/** The outline, kept live as the document changes (local and remote edits). */
export function useOutline(editor: Editor | null): OutlineItem[] {
  const [items, setItems] = useState<OutlineItem[]>([]);

  useEffect(() => {
    if (!editor) return;
    let frame = 0;
    // Collaborative typing fires `update` per keystroke from every peer; one
    // recompute per frame is plenty.
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => setItems(readOutline(editor)));
    };
    schedule();
    editor.on("update", schedule);
    return () => {
      cancelAnimationFrame(frame);
      editor.off("update", schedule);
    };
  }, [editor]);

  return editor ? items : [];
}

/** The nearest ancestor that scrolls vertically: the editor's viewport. */
function scrollParent(el: HTMLElement): HTMLElement | null {
  for (let node = el.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if (overflowY === "auto" || overflowY === "scroll") return node;
  }
  return null;
}

/** How far below the viewport top a heading may sit and still count as current. */
const ACTIVE_OFFSET_PX = 96;

/**
 * Index of the heading the reader is in: the last one whose top has scrolled
 * past a line just below the top of the viewport.
 */
function useActiveHeading(editor: Editor | null, items: OutlineItem[]): number {
  const [active, setActive] = useState(-1);

  useEffect(() => {
    if (!editor || items.length === 0) return;
    const viewport = scrollParent(editor.view.dom);
    if (!viewport) return;

    let frame = 0;
    const measure = () => {
      const line = viewport.getBoundingClientRect().top + ACTIVE_OFFSET_PX;
      let current = -1;
      for (let i = 0; i < items.length; i++) {
        const dom = editor.view.nodeDOM(items[i]!.pos);
        if (!(dom instanceof HTMLElement)) continue;
        if (dom.getBoundingClientRect().top <= line) current = i;
        else break;
      }
      setActive(current);
    };
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    onScroll();
    viewport.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      viewport.removeEventListener("scroll", onScroll);
    };
  }, [editor, items]);

  return active;
}

const INDENT: Record<number, string> = { 2: "pl-2", 3: "pl-5", 4: "pl-8" };

export function Outline({
  editor,
  onNavigate,
  className,
}: {
  editor: Editor | null;
  /** Called after a jump, e.g. so a mobile drawer can close itself. */
  onNavigate?: () => void;
  className?: string;
}) {
  const items = useOutline(editor);
  const active = useActiveHeading(editor, items);

  const jump = useCallback(
    (item: OutlineItem) => {
      if (!editor) return;
      // Let a drawer start closing first: Radix restores focus on close, and
      // doing that mid-scroll can cancel a smooth scroll in some browsers.
      onNavigate?.();
      requestAnimationFrame(() => {
        const dom = editor.view.nodeDOM(item.pos);
        if (dom instanceof HTMLElement) {
          dom.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    },
    [editor, onNavigate],
  );

  if (items.length === 0) {
    return (
      <p className={cn("px-3 py-2 text-xs text-muted-foreground", className)}>
        Headings you add to the document appear here.
      </p>
    );
  }

  return (
    <nav aria-label="Document outline" className={className}>
      <ul className="space-y-px">
        {items.map((item, i) => (
          <li key={`${i}-${item.text}`}>
            <button
              type="button"
              onClick={() => jump(item)}
              aria-current={i === active ? "location" : undefined}
              className={cn(
                "w-full text-left rounded-r-md py-1.5 pr-2 text-sm leading-snug transition-colors",
                "hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                "border-l-2 border-transparent",
                INDENT[item.level],
                item.level === 2 ? "font-medium" : "text-muted-foreground",
                i === active && "border-primary bg-muted/60 text-foreground",
              )}
            >
              <span className="line-clamp-2">{item.text}</span>
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
}
